import { IField, IOTP } from 'app/services/api5-service/api.interface';
import { createButton } from 'components/form-button/form-button.utils';
import { IFormButtonUi } from 'components/form-button/model';
import { BehaviorSubject, of } from 'rxjs';

import { getInputWidth$ } from 'common/dynamic-form/utils/get-input-width';
import { getDesc } from 'common/dynamic-form/wrappers/field-base/description/description.utils';
import { FieldSettings } from 'common/dynamic-form/wrappers/field-base/model';
import { DocHelper } from 'utils/dochelper';

import { OTPProps } from './model/otp-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
} from '../../model';

/**
 * Get submit button for otp field
 *
 * @param control
 * @param field
 */
function getSubmitButton(
  control: IOTP,
  field: IField,
): IFormButtonUi | undefined {
  const buttons = DocHelper.getArrayWrapperValue(field.button);
  const submitButton = buttons.find(
    button => button.$name === control.$on_fulfill,
  );

  return submitButton
    ? createButton({
        type: submitButton.$type,
        name: submitButton.$name,
      })
    : undefined;
}

/**
 * Get name of field with timeout value
 *
 * @param control
 * @param field
 */
function getTimeoutFieldName(control: IOTP, field: IField): string {
  return field.input!.find(f => f.$name === control.$timer)!.$name;
}

/**
 * Get props for OTP field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
function getOTPProps(
  control: IOTP,
  field: IField,
  state: ISPFormState,
): OTPProps {
  const desc = getDesc(field, state)!;
  const descSubject = new BehaviorSubject(desc);
  const buttonsSubject = new BehaviorSubject([]);

  const props: OTPProps = {
    originalControl: control,
    originalField: field,
    fieldSettings$: of({
      customButtons$: buttonsSubject,
      customDesc$: descSubject,
      displayMode: 'input-like',
      tagPlace: 'field',
    } as FieldSettings),
    placeholder$: state.mixedService.getControlPlaceholder$(
      control,
      ISPFieldType.OTP,
    ),
    width$: getInputWidth$(control),
    mask: control.$mask!,
    timeoutName: getTimeoutFieldName(control, field),
    submitButton: getSubmitButton(control, field),
    descMacros: desc.msg,
    customFieldButtons: buttonsSubject,
    customFieldDescription: descSubject,
    showSetValueError: true,
  };

  return props;
}

/**
 * Get config for OTP field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getOTPConfig(
  control: IOTP,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.OTP> {
  const props = getOTPProps(control, field, state);

  const config: ISPFieldConfig<ISPFieldType.OTP> = {
    key: control.$name,
    props,
    type: ISPFieldType.OTP,
    wrappers: [ISPFieldWrapper.FieldBase, ISPFieldWrapper.ValidationError],
  };

  return config;
}
